import * as React from 'react'
// libs
import { useSelector } from 'react-redux'
import { useMutation, useQuery } from '@apollo/client/react'
// graphql
import {
  EDIT_ACTIVITY_MUTATION,
  CREATE_ACTIVITY_MUTATION,
  DELETE_ACTIVITY_MUTATION
} from 'graphql/activities/mutations'
import { DEFAULT_QUERY } from 'graphql/default'
import { RESOURCE_DELETE_MUTATION } from 'graphql/resource/mutations'
// store
import { objectEntitiesSelector } from 'store/reducers/objectEntities'
// constants
import { OBJECT_ENTITY_NAMES } from 'constants/objects'
// types
import { TRefetch } from 'types/refetch'
import { NGetActivities } from 'types/activities/helpers'
import { NCreateReviewBL } from 'containers/Review/CreateReview/layers/BL'
import {
  ILog,
  IPlan,
  IField,
  IReview,
  IPlanKpi,
  IPlanAction,
  IPlanObjective,
  LogActionTypes,
  LogEntityTypes,
  ILogFilterInput
} from 'types/graphql'
// utils
import get from 'lodash/get'
import pipe from 'lodash/fp/pipe'
import moment from 'moment'
import orderBy from 'lodash/fp/orderBy'
import isFunction from 'lodash/isFunction'
import { getFieldsList } from 'utils/fields'
import { getActivityContentType, parseGraphQLErrorMessages, safeGetOr, sortActivitiesByDate } from 'utils/index'

export declare namespace NActivitiesListAPI {
  export interface IContext {
    data: ILog[]
    logs: [string, ILog[]][]
    entity: TEntity
    addLink: (entity: ILog | IReview, data: any) => void
    loading: boolean
    refetch: TActivitiesListRefetch
    beforeUpload: NCreateReviewBL.TBeforeUpload
    editActivity: (activity: ILog, data: any) => void
    deleteActivity: (activity: ILog, shouldCreateActivity: boolean) => void
    deleteResource: (activity: ILog) => void
    resourceFields: IField[]
    canDeleteResource: boolean
    canModifyResource: boolean
  }

  interface IIncomingProps {
    skip?: boolean
    query: any
    entity: TEntity
    variables: ILogFilterInput
    beforeUpload: NCreateReviewBL.TBeforeUpload
    canDeleteResource?: boolean
    canModifyResource?: boolean
  }

  export type TActivitiesListRefetch = TRefetch<{ logs: NGetActivities.IResponse }, NGetActivities.IVariables>
  export type TEntity = ILog & IPlan & IPlanAction & IPlanKpi & IPlanObjective
}

export const ActivitiesListAPIContext = React.createContext<NActivitiesListAPI.IContext>({} as any)

const ActivitiesListAPI: React.FunctionComponent<NActivitiesListAPI.IIncomingProps & any> = ({
  skip,
  query,
  entity,
  children,
  variables,
  beforeUpload,
  isSubActivities,
  canDeleteResource,
  canModifyResource
}) => {
  const { data, loading, refetch } = useQuery<{ logs: NGetActivities.IResponse }>(query || DEFAULT_QUERY, {
    skip,
    variables,
    fetchPolicy: 'network-only'
  })

  const objectEntities = useSelector(objectEntitiesSelector)

  const [editActivityMutation] = useMutation(EDIT_ACTIVITY_MUTATION)
  const [createActivityMutation] = useMutation(CREATE_ACTIVITY_MUTATION)
  const [deleteActivityMutation] = useMutation(DELETE_ACTIVITY_MUTATION)
  const [deleteResourceMutation] = useMutation(RESOURCE_DELETE_MUTATION)

  const resourceFields = getFieldsList(objectEntities, OBJECT_ENTITY_NAMES.RESOURCE)

  const logs: any = pipe(
    safeGetOr<any, any, any>([], 'logs.data'),
    (data: any) => {
      if (isSubActivities) {
        return orderBy(['createdAt'], ['asc'])(data)
      }
      return orderBy(['createdAt'], ['desc'])(data)
    },
    sortActivitiesByDate
  )(data)

  // methods
  const addLink = async (entity, data) => {
    try {
      let review

      if (isFunction(beforeUpload)) {
        review = await beforeUpload()
      }

      await createActivityMutation({
        variables: {
          input: {
            entityId: get(review, 'id') || entity.id,
            entityType: LogEntityTypes.Link,
            actionType: LogActionTypes.Create,
            metadata: {
              fileName: data.linkName,
              fileUrl: data.linkTarget
            }
          }
        }
      })

      await refetch()
    } catch (errors) {
      console.log('[ActivitiesListAPI.addLink]: ', parseGraphQLErrorMessages(errors))
    }
  }

  const deleteActivity = async (activity, shouldCreateActivity) => {
    try {
      await deleteActivityMutation({
        variables: { id: activity.id }
      })

      if (shouldCreateActivity) {
        await createActivityMutation({
          variables: {
            input: {
              entityId: entity.id,
              entityType: activity.entityType,
              actionType: LogActionTypes.Delete as any,
              metadata: {
                fileName:
                  get(activity, 'metadata.fileName') ||
                  get(activity, 'metadata.fileUrl') ||
                  `comment from ${moment(get(activity, 'createdAt')).format('DD.MM.YYYY h:mma zz')}`,
                fileContentType: getActivityContentType(activity)
              }
            }
          }
        })
      }

      await refetch()
    } catch (errors) {
      console.log('[ActivitiesListAPI.deleteActivity]: ', parseGraphQLErrorMessages(errors))
    }
  }

  const deleteResource = async (activity) => {
    try {
      await deleteResourceMutation({
        variables: {
          id: get(activity, 'metadata.resource.id') || activity.id
        }
      })

      await refetch()
    } catch (errors) {
      console.log('[ActivitiesListAPI.deleteResource]: ', parseGraphQLErrorMessages(errors))
    }
  }

  const editActivity = async (activity, data) => {
    try {
      await editActivityMutation({
        variables: {
          id: activity.id,
          input: {
            actionType: activity.actionType,
            metadata: {
              fileName: data.linkName || data.attachmentName,
              fileUrl: data.linkTarget || activity.metadata.fileUrl,
              fileContentType: activity.metadata.fileContentType
            }
          }
        }
      })

      await refetch()
    } catch (errors) {
      console.log('[ActivitiesListAPI.editActivity]: ', parseGraphQLErrorMessages(errors))
    }
  }

  const value = {
    data: get(data, 'logs.data', []),
    logs: logs || [],
    entity,
    addLink,
    loading,
    refetch,
    beforeUpload,
    editActivity,
    resourceFields,
    deleteActivity,
    deleteResource,
    canDeleteResource,
    canModifyResource
  }

  return <ActivitiesListAPIContext.Provider value={value}>{children}</ActivitiesListAPIContext.Provider>
}

export default ActivitiesListAPI
